import type { LocalTradeFilters } from '@/types';

/**
 * Map table column ID to sidebar filter section ID.
 * Column IDs that don't have a corresponding filter are not included.
 */
export const TRADES_COLUMN_ID_TO_FILTER_ID: Record<string, string> = {
  status_display: 'statuses',
  counterparty: 'counterparties',
  symbol_id: 'symbols',
  trade_type_display: 'tradeTypes',
  collateral_type: 'collateralTypes',
  agreement_display: 'agreementTypes',
  term_type_display: 'termTypes',
  quantity: 'quantity',
  contract_value: 'contractValue',
  margin: 'margin',
  trade_date: 'tradeDate',
  settlement_date: 'settlementDate',
  maturity_date: 'maturityDate',
  gross_price: 'grossPrice',
  daily_earnings: 'dailyEarnings',
  created_at: 'createdAt',
  updated_at: 'updatedAt',
  currency: 'currencies',
  // LR-474 (D3): the Settlement Ccy column is deliberately NOT mapped — its
  // filter (settlementCurrencies) lives in the summary-bar currency pill, not
  // the sidebar, so a "Filter by Settlement Ccy" entry would open the sidebar
  // to nothing (the exact dead-click the MTM map comment below warns about).
};

/** Set of column IDs that have a corresponding filter. */
export const TRADES_FILTERABLE_COLUMN_IDS = new Set(Object.keys(TRADES_COLUMN_ID_TO_FILTER_ID));

/** Reverse mapping: filter section ID → table column ID. */
export const TRADES_FILTER_ID_TO_COLUMN_ID: Record<string, string> = Object.fromEntries(
  Object.entries(TRADES_COLUMN_ID_TO_FILTER_ID).map(([colId, filterId]) => [filterId, colId])
);

/**
 * MTM-tab variant (LR-394). Mirrors the Trades map except the Counterparty
 * filter is omitted — the active subtab fixes the counterparty, so keeping that
 * column out prevents a "Filter by …" dead click. locate / isColumnVisible /
 * section-order / right-click all resolve against this map on MTM. Collateral
 * Type gained an MTM filter section in LR-444 (parity with Trades), so it IS
 * mapped here. LR-522: `gross_price` ("Trade Gross Price") is now a real column
 * on BOTH grids, so the grossPrice filter maps to it here too — the old
 * `cob_price → grossPrice` special-case (from when `gross_price` was MtM-only)
 * is gone; COB Gross Price has no filter section of its own.
 */
export const MTM_COLUMN_ID_TO_FILTER_ID: Record<string, string> = {
  status_display: 'statuses',
  symbol_id: 'symbols',
  trade_type_display: 'tradeTypes',
  collateral_type: 'collateralTypes',
  agreement_display: 'agreementTypes',
  term_type_display: 'termTypes',
  quantity: 'quantity',
  contract_value: 'contractValue',
  margin: 'margin',
  trade_date: 'tradeDate',
  settlement_date: 'settlementDate',
  maturity_date: 'maturityDate',
  gross_price: 'grossPrice',
  daily_earnings: 'dailyEarnings',
  created_at: 'createdAt',
  updated_at: 'updatedAt',
  currency: 'currencies',
  // settlement_currency intentionally unmapped — see the Trades map note (LR-474).
};

/** Set of column IDs that have a corresponding filter on the MTM tab. */
export const MTM_FILTERABLE_COLUMN_IDS = new Set(Object.keys(MTM_COLUMN_ID_TO_FILTER_ID));

/** Reverse mapping for MTM: filter section ID → table column ID. */
export const MTM_FILTER_ID_TO_COLUMN_ID: Record<string, string> = Object.fromEntries(
  Object.entries(MTM_COLUMN_ID_TO_FILTER_ID).map(([colId, filterId]) => [filterId, colId])
);

/**
 * Returns column IDs that currently have an active filter.
 */
export const getActiveTradesFilterColumnIds = (
  filters: LocalTradeFilters,
): string[] => {
  const active: string[] = [];

  if (filters.statuses.length > 0) active.push('status_display');
  if (filters.counterparties.length > 0) active.push('counterparty');
  if (filters.symbols.length > 0) active.push('symbol_id');
  if (filters.tradeTypes.length > 0) active.push('trade_type_display');
  if (filters.collateralTypes.length > 0) active.push('collateral_type');
  if (filters.agreementTypes.length > 0) active.push('agreement_display');
  if (filters.termTypes.length > 0) active.push('term_type_display');
  if (filters.quantityMin !== null || filters.quantityMax !== null) active.push('quantity');
  if (filters.contractValueMin !== null || filters.contractValueMax !== null) active.push('contract_value');
  if (filters.marginMin !== null || filters.marginMax !== null) active.push('margin');
  if (filters.tradeDateFrom || filters.tradeDateTo) active.push('trade_date');
  if (filters.settlementDateFrom || filters.settlementDateTo) active.push('settlement_date');
  if (filters.maturityDateFrom || filters.maturityDateTo) active.push('maturity_date');
  if (filters.grossPriceMin !== null || filters.grossPriceMax !== null) active.push('gross_price');
  if (filters.dailyEarningsMin !== null || filters.dailyEarningsMax !== null) active.push('daily_earnings');
  if (filters.createdAtFrom || filters.createdAtTo) active.push('created_at');
  if (filters.updatedAtFrom || filters.updatedAtTo) active.push('updated_at');
  if (filters.currencies.length > 0) active.push('currency');
  // settlementCurrencies has no column-header affordance (the funnel icon only
  // renders for FILTERABLE columns) — its active state shows via the pill
  // label + the applied-filters chip instead.

  return active;
};
